import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { Event, Area, ManagementAnalyticsMetaProps, AnalyticsTab, Shift, Training } from '../data/types'
import { LoadingSpinner } from '../components'
import { API_BASE_URL, ANALYTICS_TABS, TrainingsOverviewMeta, VolunteerOverviewMeta, DashboardMeta, VolunteerStatsTableOverviewMeta, TrainingsOverviewMeta as TrainingsMeta, DidarAcknowledgementOverviewMeta, DutyStatusOverviewMeta } from '../data'
import { ChevronLeftIcon } from '../icons'
import { Dashboard } from './Dashboard'
import { VolunteerOverview } from './VolunteerOverview'
import { TrainingsOverview } from './TrainingsOverview'
import { VolunteerStatsTable } from './VolunteerStatsTable'
import { DidarAcknowledgementOverview } from './DidarAcknowledgementOverview'
import { DutyStatusOverview } from './DutyStatusOverview'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'

export const ManagementAnalytics = ({ meta }: { meta: ManagementAnalyticsMetaProps }) => {
    const [allEvents, setAllEvents] = useState<Event[]>([])
    const [allTrainings, setAllTrainings] = useState<Training[]>([])

    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
    const [selectedArea, setSelectedArea] = useState<Area | null>(null)
    const [selectedShift, setSelectedShift] = useState<Shift | null>(null)
    const [selectedTraining, setSelectedTraining] = useState<Training | null>(null)

    const [activeTab, setActiveTab] = useState<AnalyticsTab>("dashboard")
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)



    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: "include" })
                if (!response.ok) throw new Error("Failed to load events.")
                const data = await response.json()
                setAllEvents(data)
            } catch { setError("Failed to load events") }
        }

        const fetchTrainings = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/training`, { credentials: "include" })
                if (!response.ok) throw new Error("Failed to load trainings.")
                const data = await response.json()
                setAllTrainings(data)
            } catch { setError("Failed to load trainings") }
        }

        const loadData = async () => {
            setLoading(true)
            await Promise.all([fetchEvents(), fetchTrainings()])
            setLoading(false)
        }

        loadData()
    }, [])

    useEffect(() => {
        setSelectedArea(null);
        setSelectedEvent(null);
        setSelectedShift(null);
        setSelectedTraining(null);
    }, [activeTab])

    const handleEventChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const eventId = Number(e.target.value)
        setSelectedArea(null);
        setSelectedShift(null);
        setSelectedEvent(allEvents.find(ev => ev.id === eventId) || null)
    }

    const handleAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const areaId = Number(e.target.value)
        setSelectedShift(null);
        setSelectedArea(selectedEvent?.areas?.find(a => a.id === areaId) || null)
    }

    const handleShiftChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const shiftId = Number(e.target.value)
        setSelectedShift(selectedArea?.shifts.find(s => s.id === shiftId) || null)
    }

    const handleTrainingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const trainingId = Number(e.target.value)
        setSelectedTraining(allTrainings?.find(t => t.id === trainingId) || null)
    }

    const selectContentPage = (tab: string) => {
        switch (tab) {
            case "volunteers":
                return <VolunteerOverview meta={VolunteerOverviewMeta} event={selectedEvent} area={selectedArea} shift={selectedShift} />
            case "trainings":
                return <TrainingsOverview meta={TrainingsOverviewMeta} training={selectedTraining} event={selectedEvent} area={selectedArea} shift={selectedShift} />
            case "volunteertable":
                return <VolunteerStatsTable meta={VolunteerStatsTableOverviewMeta} event={selectedEvent} area={selectedArea} shift={selectedShift} />
            case "didar":
                return <DidarAcknowledgementOverview meta={DidarAcknowledgementOverviewMeta} />
            case "iff":
                return <VolunteerOverview meta={VolunteerOverviewMeta} event={selectedEvent} area={selectedArea} shift={selectedShift} iff={true} />
            case "non-iiuk":
                return <VolunteerOverview meta={VolunteerOverviewMeta} event={selectedEvent} area={selectedArea} shift={selectedShift} non_iiuk={true} />
            case "duty_acceptance":
                return <DutyStatusOverview meta={DutyStatusOverviewMeta} />
            case "check-in":
                return <VolunteerOverview meta={VolunteerOverviewMeta} event={selectedEvent} area={selectedArea} shift={selectedShift} checkin={true} />
            default:
                return <Dashboard meta={DashboardMeta} event={selectedEvent} area={selectedArea} shift={selectedShift} />;
        }
    }

    return (
        <div className={`flex-1 px-4 py-8 w-full max-w-[98%] mx-auto`}>
            <div className="mb-6">
                <Link to="/admin" className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400">
                    <ChevronLeftIcon className="w-5 h-5 me-2" />
                    Back to Admin Hub
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 items-start">
                {/* LEFT ASIDE */}
                <aside className="space-y-6 sticky top-20 self-start">
                    <div className="bg-white dark:bg-gray-800 rounded-xl border p-5 space-y-5 shadow-sm dark:border-gray-600">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{meta.title}</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{meta.description}</p>
                        </div>

                        <div className="space-y-1 border-t-2 pt-3">
                            {ANALYTICS_TABS.map((tab, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium text-start
                                        ${activeTab === tab.key ? "bg-primary-600 text-white" : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"}`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                            {

                            }
                        </div>

                        <div className="border-t-2 pt-3">
                            {activeTab === 'trainings' ? (
                                <>
                                    <div className="mb-4">
                                        <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Training</label>
                                        <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleTrainingChange} value={selectedTraining?.id ?? ""}>
                                            <option value="">All Trainings</option>
                                            {allTrainings.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
                                        </select>
                                    </div>
                                    <div className="mb-4">
                                        <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Event</label>
                                        <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleEventChange} value={selectedEvent?.id ?? ""}>
                                            <option value="">All Events</option>
                                            {sortByName(allEvents).map(e => (<option key={e.id} value={e.id}>{e.name}</option>))}
                                        </select>
                                    </div>
                                    {selectedEvent && (
                                        <div className="mb-4">
                                            <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Area</label>
                                            <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleAreaChange} value={selectedArea?.id ?? ""}>
                                                <option value="">All Areas</option>
                                                {sortByName(selectedEvent.areas).map(a => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                            </select>
                                        </div>
                                    )}
                                    {selectedArea && selectedEvent && (
                                        <div className="mb-4">
                                            <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Shift</label>
                                            <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleShiftChange} value={selectedShift?.id ?? ""}>
                                                <option value="">All Shifts</option>
                                                {sortShifts(selectedArea.shifts).map(s => (<option key={s.id} value={s.id}>{getShiftDisplayName(s)}</option>))}
                                            </select>
                                        </div>
                                    )}
                                </>
                            ) : (!(activeTab === 'didar' || activeTab === 'duty_acceptance') && (
                                <>
                                    <div className="mb-4">
                                        <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Event</label>
                                        <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleEventChange} value={selectedEvent?.id ?? ""}>
                                            <option value="">All Events</option>
                                            {sortByName(allEvents).map(e => (<option key={e.id} value={e.id}>{e.name}</option>))}
                                        </select>
                                    </div>
                                    {selectedEvent && (
                                        <div className="mb-4">
                                            <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Area</label>
                                            <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleAreaChange} value={selectedArea?.id ?? ""}>
                                                <option value="">All Areas</option>
                                                {sortByName(selectedEvent.areas).map(a => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                            </select>
                                        </div>
                                    )}
                                    {selectedArea && selectedEvent && activeTab !== 'volunteertable' && (
                                        <div className="mb-4">
                                            <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Shift</label>
                                            <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleShiftChange} value={selectedShift?.id ?? ""}>
                                                <option value="">All Shifts</option>
                                                {sortShifts(selectedArea.shifts).map(s => (<option key={s.id} value={s.id}>{getShiftDisplayName(s)}</option>))}
                                            </select>
                                        </div>
                                    )}
                                </>)
                            )}
                        </div>
                    </div>
                </aside>

                {/* RIGHT CONTENT - min-w-0 prevents overflow issues */}
                <main className="space-y-6 pt-0 mt-0 min-w-0 overflow-x-auto">
                    {loading && <LoadingSpinner meta={{ loadingText: meta.loadingText }} />}
                    {error && <div className="text-danger-500">{error}</div>}
                    {!loading && !error && selectContentPage(activeTab)}
                </main>
            </div>
        </div>
    )
}
